import type { ResumeEntry, ResumePayload } from "#types/types.ts";

export type ToolApprovalChoice = "allow" | "alwaysAllow" | "deny";

/** A resolved per-call approval choice, converted to a resume entry by `buildResumeEntry`. */
export interface ToolApprovalChoiceInput {
    choice: ToolApprovalChoice;
    editedArgs?: Record<string, unknown>;
}

// Edits ride only on an approval, and only when there are some — a decision without edits stays
// byte-identical to the pre-edit wire shape.
function editedArgsField(choice: ToolApprovalChoice, editedArgs?: Record<string, unknown>): Pick<ResumePayload, "editedArgs"> {
    return choice !== "deny" && editedArgs !== undefined && Object.keys(editedArgs).length > 0 ? { editedArgs } : {};
}

/**
 * Build a single native AG-UI `ResumeEntry` for the batch `resume` post. `interruptId` is the real interrupt id, carried
 * verbatim from the live interrupt or the replayed record.
 * - allow        → payload.approved true,  rememberForConversation false
 * - alwaysAllow  → payload.approved true,  rememberForConversation true
 * - deny         → payload.approved false
 */
export function buildResumeEntry(
    { interruptId, choice, editedArgs }: ToolApprovalChoiceInput & { interruptId: string }
): ResumeEntry {
    const payload: ResumePayload = {
        approved: choice !== "deny",
        rememberForConversation: choice === "alwaysAllow",
        ...editedArgsField(choice, editedArgs)
    };

    return { interruptId, status: "resolved", payload };
}

/** A pending call, reduced to what a decision needs. */
export interface DecidableCall {
    toolCallId: string;
    interruptId?: string | null;
    editedArgs?: Record<string, unknown>;
}

/** A resolved per-call choice, ready to convert to a resume entry. */
export interface ResolvedChoice extends DecidableCall {
    choice: ToolApprovalChoice;
}

/** A single row's decision. Always-allow is a whole-card action, never per row. */
export type RowDecision = "allow" | "deny";

/**
 * Apply one choice to every pending call — the whole-card actions (allow / always allow / deny all).
 * Always-allow is granted per conversation and per *tool name*, so the backend ORs the flag across
 * the batch anyway; stamping every call keeps the request and the grant in agreement.
 */
export function buildBatchChoices(calls: DecidableCall[], choice: ToolApprovalChoice): ResolvedChoice[] {
    return calls.map(({ toolCallId, interruptId, editedArgs }) => ({ toolCallId, interruptId, editedArgs, choice }));
}

/**
 * Turn per-row decisions into a full choice set, or null while any row is still undecided.
 *
 * A resume must resolve exactly the pending calls — a partial set is rejected — so the caller posts
 * only on a non-null result. Encoding that here keeps the rule in one named place instead of an
 * inline "all decided" check next to the submit.
 */
export function resolveRowChoices(
    calls: DecidableCall[],
    decisions: Record<string, RowDecision | undefined>
): ResolvedChoice[] | null {
    const resolved: ResolvedChoice[] = [];
    for (const { toolCallId, interruptId, editedArgs } of calls) {
        const decision = decisions[toolCallId];
        if (decision === undefined) {
            return null;
        }
        resolved.push({ toolCallId, interruptId, editedArgs, choice: decision });
    }

    return resolved;
}

/**
 * The third-party the call writes through, as an `INTEGRATION_MANIFESTS` key, or null.
 *
 * The provider rides in the call's own args (`calendar_integration_type` is `"Outlook"`/`"Google"`),
 * so it is read rather than derived from the tool name. Workleap-native writes (goals, reviews) name
 * no provider and get no badge.
 */
export function resolveCallIntegrationKey(args: Record<string, unknown>): string | null {
    const raw = args["calendar_integration_type"];

    return typeof raw === "string" && raw.trim() !== "" ? raw.trim().toLowerCase() : null;
}

/** Marks a call that names no third party; it still counts as a distinct source of writes. */
export const WORKLEAP_INTEGRATION_KEY = "workleap";

/**
 * The distinct providers a batch writes through, in first-seen order.
 *
 * A batch is only whatever the model proposed in one turn, so it can mix providers. More than one
 * key means no single badge can describe it honestly.
 */
export function resolveBatchIntegrationKeys(calls: { args: Record<string, unknown> }[]): string[] {
    const keys: string[] = [];
    for (const { args } of calls) {
        const key = resolveCallIntegrationKey(args) ?? WORKLEAP_INTEGRATION_KEY;
        if (!keys.includes(key)) {
            keys.push(key);
        }
    }

    return keys;
}

/** "submit_review" → "Submit review" (fallback display for a raw tool name). */
export function humanizeToolName(toolName: string): string {
    const words = toolName.split(/[_\s]+/).filter(Boolean);
    if (words.length === 0) {
        return toolName;
    }

    return words
        .map((word, index) => (index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word))
        .join(" ");
}

/**
 * The LLM fills two status fields (core-python `StatusFieldsMixin`) that ride *inside* a tool
 * call's `args`, alongside the real parameters. Pull them out for display on the approval card.
 *
 * - `title`  → trimmed `status_title`, or `null` when the tool didn't emit one (caller shows a
 *   generic fallback label). Plain text, no markdown.
 * - `detail` → trimmed `status_detail`, or `null` when absent/blank/non-string. May carry
 *   `**bold**` markdown.
 */
export function extractToolCallStatus(args: Record<string, unknown>): { title: string | null; detail: string | null } {
    const rawTitle = args["status_title"];
    const rawDetail = args["status_detail"];
    const title = typeof rawTitle === "string" && rawTitle.trim() !== "" ? rawTitle.trim() : null;
    const detail = typeof rawDetail === "string" && rawDetail.trim() !== "" ? rawDetail.trim() : null;

    return { title, detail };
}
